import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import classNames from 'classnames';
import { motion } from 'framer-motion';
import React, { forwardRef, Suspense, lazy } from 'react';
import Default from 'react-bootstrap/Container';
import { Media } from '../../media/media.esm.js';

const FloatingOrnaments = /*#__PURE__*/ lazy(()=>import('../floating-ornaments/floating-ornaments.esm.js'));
const AnimationPresets = {
    fadeInScaleDown: 'fadeInScaleDown'
};
/**
 * This function will return a CSSStyleDeclaration object that can be used
 * to set the background color of the container and allow children components
 * to access the background color of the container via the
 * `--wcds-container-background-color` CSS variable.
 */ const getContainerCSSDeclaration = (style = {})=>{
    if (!style) {
        return {};
    }
    return {
        /**
         * Import SASS Variable
         */ '--wcds-container-background-color': style?.backgroundColor && `var(${style.backgroundColor})`,
        '--wcds-container-color': style?.color && `var(${style?.color})`,
        'backgroundColor': style?.backgroundColor && `var(--wcds-container-background-color)`,
        'color': style?.color && `var(--wcds-container-color)`,
        'cursor': style?.cursor
    };
};
const BackgroundImage = ({ media })=>{
    if (!media) {
        return jsx(Fragment, {});
    }
    return jsx("div", {
        className: "position-absolute w-100 h-100 bg-black",
        children: jsx(Media, {
            hideBorder: true,
            media: media,
            className: "w-100 h-100 opacity-40",
            objectFit: "cover"
        })
    });
};
/**
 * Gradually fades in the element and reduces its scale from 1.05 to 1 when the
 * component is scrolled into view.
 */ const fadeInScaleDownAnimation = {
    initial: {
        opacity: 0,
        y: 48,
        scale: 1.05
    },
    whileInView: {
        opacity: 1,
        y: 0,
        scale: 1,
        transition: {
            ease: [
                0.16,
                1,
                0.3,
                1
            ],
            duration: 2
        }
    },
    viewport: {
        once: true
    }
};
const animations = {
    fadeInScaleDown: fadeInScaleDownAnimation
};
const AnimationContainer = ({ animationType, children })=>{
    let animation;
    if (animationType && animations[animationType]) {
        animation = animations[animationType];
    }
    if (!animationType || !animation) {
        return jsx(Fragment, {
            children: children
        });
    }
    return jsx(motion.div, {
        ...animation,
        children: children
    });
};
/**
 * Prevent scroll issues from off-screen floating ornaments
 */ const FloatingOrnamentsContainer = ({ children })=>{
    return jsx("div", {
        className: "overflow-hidden",
        children: children
    });
};
/**
 * This Container is our enhanced version of the Bootstrap Container component
 * that will provide us with consistent layout options across the application.
 *
 * The main enhancements for this component are the ability to set a background
 * color for fluid containers whilst maintaining the max width of the breakpoint.
 */ const _Container = /*#__PURE__*/ forwardRef(({ className, spacingGroup, color: _color, style, media, children, floatingOrnaments, animationType, mediaAspectRatio: _mediaAspectioRatio, ...rest }, ref)=>{
    const hasFloatingOrnaments = !!floatingOrnaments && floatingOrnaments.length > 0;
    const Wrapper = hasFloatingOrnaments ? FloatingOrnamentsContainer : React.Fragment;
    return jsx(Wrapper, {
        children: jsx(Default, {
            className: classNames('position-relative', className, spacingGroup),
            ref: ref,
            style: getContainerCSSDeclaration(style),
            ...rest,
            children: jsxs(AnimationContainer, {
                animationType: animationType,
                children: [
                    jsx(BackgroundImage, {
                        media: media
                    }),
                    hasFloatingOrnaments ? jsx(Suspense, {
                        fallback: jsx(Fragment, {}),
                        children: jsx(FloatingOrnaments, {
                            floatingOrnaments: floatingOrnaments
                        })
                    }) : undefined,
                    children
                ]
            })
        })
    });
});
const FluidWithBackground = /*#__PURE__*/ forwardRef(({ className, spacingGroup, color: _color, style, media, ...rest }, ref)=>{
    return jsxs(Default, {
        fluid: true,
        ref: ref,
        className: "position-relative p-0",
        style: getContainerCSSDeclaration(style),
        children: [
            jsx(BackgroundImage, {
                media: media
            }),
            jsx(Container, {
                className: classNames(className, spacingGroup),
                ...rest
            })
        ]
    });
});
const customComponents = {
    /**
     * Custom components
     */ FluidWithBackground
};
const Container = Object.assign(_Container, customComponents);

export { AnimationPresets, Container, _Container };
